/**
 * mc-overlay-misc - a grab bag of somewhat useful utility functions for overlay
 *
 * Copyright (c) 2019. Mirae Climate Co., Ltd.
 */

import µ from "../../earth/1.0.0/micro";
import * as d3 from "d3";
import { geoStitch } from "../../lib/d3.geo.projection/2.4.0/d3-geo-projection.repack";
// import simplify from "../../lib/simplify/1.2.3/simplify";
import mcUtils from "./mc-utils";

export default (function () {
    // const OVERLAY_ALPHA = Math.floor(0.65 * 255);
    var view = µ.view();

    function drawContour(projectionType, grid, projection, units, showContourLabel, renderMode) {
        let ctx = d3.select("#contour").node().getContext("2d");
        const productType = grid.productType || "";
        // const epsilon = 0.;
        let header = grid.header;
        let λ0 = header.lo1, /*λ1 = header.lo2,*/ φ0 = header.la1;  // the grid's origin (e.g., 0.0E, 90.0N)
        // const epsilon = (360. - λ1 + λ0) / 360.;
        const epsilon = 0.;
        φ0 = φ0 < 0 ? -φ0 : φ0;
        const Δλ = header.dx + epsilon;
        // var Δφ = header.dy;    // distance between grid points (e.g., 2.5 deg lon, 2.5 deg lat)
        const ni = header.nx, njj = header.ny;    // number of grid points W-E and N-S (e.g., 144 x 73)
        const offsetLo = Δλ / 2 - λ0;
        const widthLo = 360;
        // const lo2 = header.lo2;
        const lo1 = header.lo1;

        if (header === "") return;

        let rangeStart = grid.scale.bounds[0],
            rangeStop = grid.scale.bounds[1],
            step = d3.select("#isoline-thresholds").node().value;

        let diff = rangeStop - rangeStart,
            thresholdsMax = diff / 100.,
            thresholdsValue = thresholdsMax * step;

        thresholdsValue = renderMode === "ci" ? 0.4 : thresholdsValue; // if ci and mslp, thresholds 4,

        let loRotate = lo1 - 180, loStart = lo1 - 180, loStop = lo1 + 180,
            loStartOffset = loStart + epsilon, loStopOffset = loStop - epsilon;

        let contours = d3.contours()
            .size([ni + 1, njj])
            .smooth(true)
            .thresholds(d3.range(rangeStart, rangeStop, thresholdsValue));;

        let contourPath = d3.geoPath()
            .projection(projection)
            .context(ctx);

        let overlayGridPoints = [];
        let ix = 0;
        // let currentScale = grid.scale;

        grid.forEachPoint2(function (λ, φ, d) {
            if (µ.isValue(d)) {
                overlayGridPoints[ix++] = d;
            }
        });

        let tempMax, tempMin, tempCount = 0;

        grid.forEachPoint(function (λ, φ, d) {
            if (tempCount === 0) {
                tempMax = d;
                tempMin = d;
            }
            if (µ.isValue(d)) {
                if (tempMax < d) {
                    tempMax = d;
                }
                if (tempMin > d) {
                    tempMin = d;
                }
            }
            tempCount++;
        });

        overlayGridPoints = rotate(overlayGridPoints);
        overlayGridPoints = cylinder(overlayGridPoints);

        // convert 0~360 to -180~180
        function rotate(values) {
            let v = new Float32Array(njj * ni);
            for (let j = 0; j < njj; j++) {
                for (let i = 0; i < ni; i++) {
                    let value = values[j * ni + i];
                    if (value.length === 2) { // velocity
                        v[j * ni + i] = Math.sqrt(value[0] * value[0] + value[1] * value[1]);
                    } else {
                        v[j * ni + i] = value;
                    }
                }
            }

            let l = ni >> 1;
            for (let j = 0, k = 0; j < njj; ++j, k += ni) {
                v = reverseArr(v.subarray(k, k + l), v, k, k + l);
                v = reverseArr(v.subarray(k + l, k + ni), v, k + l, k + ni);
                v = reverseArr(v.subarray(k, k + ni), v, k, k + ni);
            }
            return v;
        }

        // reverse array not available on IE
        function reverseArr(input, source, start, end) {
            let v = new Float32Array(input.length);

            for (let i = input.length - 1, j = 0; i >= 0; i--) {
                v[j++] = input[i];
            }

            for (let i = 0, k = 0; i < source.length; i++) {
                if (i >= start && i <= (end - 1)) {
                    source[i] = v[k++];
                }
                if (i > (end - 1)) {
                    break;
                }
            }

            return source;
        }


        // duplicate left column to the right for stitch
        function cylinder(values) {
            let v = new Float32Array(njj * (ni + 1));
            for (let j = 0; j < njj; j++) {
                for (let i = 0; i < ni; i++) {
                    v[j * (ni + 1) + i] = values[j * ni + i];
                }
                v[j * (ni + 1) + ni] = values[j * ni];
            }
            return v;
        }

        // function smoothPath(pstr) {
        //     let interpolate = d3.line()
        //         .x(d => d[0])
        //         .y(d => d[1])
        //         .curve(d3.curveCatmullRomClosed)
        //         .context(ctx);
        //     let contourLines = contourPath(pstr);
        //     if (contourLines !== null) {
        //         contourLines = contourLines.split("M");
        //         contourLines.shift();
        //         contourLines.forEach(
        //             d => {
        //                 let points = d.replace(/Z/, "").split("L").map(d => d.split(","));
        //                 // interpolate(points);
        //                 interpolate(simplify(points, 3));
        //             }
        //         );
        //     }
        // }

        contours(overlayGridPoints)
            .map(function (d) { // invert x/y pixel points to lon/lan coords
                var shared = {};
                var p = {
                    type: "Polygon",
                    coordinates: d3.merge(d.coordinates.map(function (polygon) {
                        return polygon.map(function (ring) {
                            return ring.map(function (point) {
                                // var coordX = point[0] / (ni + 1) * lo2 - loRotate;
                                return [point[0] / (ni + 1) * widthLo + loRotate, φ0 - point[1] / njj * 180];
                                // return [point[0] / (ni + 1) * widthLo, φ0 - point[1] / njj * 180];
                            }).reverse();
                        });
                    }))
                };

                // // // Add contact points with the antimeridian.
                p.coordinates = p.coordinates
                    .map(function (ring) {
                        ring.pop();
                        var r = [];
                        for (var i = 0, l = ring.length; i < l; i++) {
                            var p = ring[i];
                            if (p[0] === loStop && ring[(i + l - 1) % l][0] < loStop)
                                r.push([p[0], p[1] + epsilon]);
                            if (p[0] === loStart && ring[(i + l - 1) % l][0] > loStart)
                                r.push([p[0], p[1] - epsilon]);
                            r.push(p);
                            if (p[0] === loStop && ring[(i + 1) % l][0] < loStop)
                                r.push([p[0], p[1] - epsilon]);
                            if (p[0] === loStart && ring[(i + 1) % l][0] > loStart)
                                r.push([p[0], p[1] + epsilon]);
                        }
                        r.push(r[0]);
                        return r;
                    });

                // Record the y-intersections with the antimeridian.
                p.coordinates.forEach(function (ring) {
                    ring.forEach(function (p) {
                        if (p[0] === loStart || p[0] === loStop) {
                            shared[p[1]] |= p[0] === loStart ? 1 : 2;
                        }
                    });
                });

                // Offset any unshared antimeridian points to prevent their stitching.
                p.coordinates.forEach(function (ring) {
                    ring.forEach(function (p) {
                        if ((p[0] === loStart || p[0] === loStop) && shared[p[1]] !== 3) {
                            p[0] = p[0] === loStart ? loStartOffset : loStopOffset;
                        }
                    });
                });

                p = geoStitch(p, loStart, loStop);

                // After stitching remove the shared points -- they are off
                // see https://github.com/d3/d3-contour/issues/25
                // The code below does not work for ocn type (high resolution)
                if (productType !== "ocn") {
                    p.coordinates = p.coordinates
                        .map(function (ring) {
                            ring.pop();
                            ring = ring
                                .map(function (p) {
                                    var p0 = (p[0] + loStop) * (ni + 1) / (ni) + loStart - offsetLo; // add offsetLo to adop lo1 coord value
                                    return [p0, p[1]];
                                });
                            ring = ring.filter(function (p) {
                                return Math.abs(p[0]) < loStopOffset;
                            });
                            if (ring[0]) {
                                ring.push(ring[0]);
                            }
                            return ring;
                        })
                        .filter(function (ring) {
                            return ring.length > 0;
                        });
                }

                // If the MultiPolygon is empty, treat it as the Sphere.
                return p.coordinates.length
                    ? { type: "Polygon", coordinates: p.coordinates, value: d.value }
                    : { type: "Sphere", value: d.value };

            })
            .forEach(function (geometry, i) { // draw geometry coords to path (projection, canvas context)
                if (geometry.type === "Polygon") {
                    ctx.beginPath();
                    contourPath(geometry);
                    // smoothPath(geometry);
                    ctx.closePath();
                    ctx.lineJoin = "round";
                    ctx.lineWidth = 0.9;
                    // var gradient = currentScale.gradient(geometry.value, OVERLAY_ALPHA);
                    // var strokeStyle = "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")"; //"rgba(170,240,250,1.0)";
                    // var fillStyle = "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")"; //"rgba(170,240,250,1.0)";
                    var strokeStyle = "rgba(190,240,240,1.0)";
                    var fillStyle = "rgba(190,240,240,1.0)";
                    ctx.strokeStyle = strokeStyle;
                    ctx.stroke();

                    if (showContourLabel && projection.scale() > 100.) {
                        fillLabel(projection, geometry, strokeStyle, fillStyle);
                    }
                }
            });

        if (projectionType !== "orthographic") {
            maskOutBoundsOfMap(projection, ctx);
        }

        function getAngle(coords) {
            let x1 = coords.pos2[0],
                x2 = coords.pos1[0],
                y1 = coords.pos2[1],
                y2 = coords.pos1[1];
            let dx = x2 - x1;
            let dy = y2 - y1;
            let rad = Math.atan2(dy, dx);
            return Math.abs(rad) > (Math.PI / 2) ? rad - Math.PI : rad;
        }

        function fillLabel(currentProjection, geometry, strokeStyle, fillStyle) {
            let ctx = d3.select("#contour").node().getContext("2d");
            let centerCoord = currentProjection.invert([view.width / 2, view.height / 2]);

            for (let i = 0; geometry.type === "Polygon" && i < geometry.coordinates.length; i++) {
                let offSet = (i % geometry.coordinates.length) * 5; // random to pervent the same vertical line
                let sourceCoord = centerProximateCoord(geometry.coordinates[i], offSet, projection, centerCoord);

                let distance = d3.geoDistance(sourceCoord.sourceCoord, centerCoord);
                if (projectionType === "orthographic" && distance > 1.34) {
                    continue;
                }

                let pos1 = currentProjection(sourceCoord.sourceCoord);
                let pos2 = currentProjection(sourceCoord.prevSourceCoord);
                let text = µ.formatScalar(geometry.value, units);
                text = parseFloat(text).toFixed(0);

                ctx.save();
                ctx.translate(pos1[0], pos1[1]);
                ctx.rotate(getAngle({ pos1: pos1, pos2: pos2 }));

                ctx.strokeStyle = strokeStyle;
                ctx.fillStyle = fillStyle;
                mcUtils.roundRect(ctx, -5 - ctx.measureText(text).width / 2, -6, 7 + ctx.measureText(text).width, 10, 4, true, true);

                ctx.font = "0.6em Arial";
                ctx.fillStyle = "rgba(10,10,10,1.0)";
                // ctx.fillStyle = "rgba(240,240,240,1.0)";
                ctx.fillText(text, -2 - ctx.measureText(text).width / 2, 2);
                ctx.restore();
            }
        }

        function centerProximateCoord(coordinates, offSet, currentProjection, centerCoord) {
            let sourceCoord = coordinates.pop(),
                prevSourceCoord = sourceCoord,
                prevSourceCoord2 = sourceCoord,
                prevPosition = currentProjection(sourceCoord),
                position;
            coordinates.forEach(function (coord) {
                position = currentProjection(coord);
                if (Math.abs(position[1]) < Math.abs(prevPosition[1])) {
                    sourceCoord = coord;
                    prevPosition = position;
                    prevSourceCoord2 = prevSourceCoord;
                }
                prevSourceCoord = coord;
            });

            return {
                sourceCoord: sourceCoord,
                prevSourceCoord: prevSourceCoord2
            };
        }

        function maskOutBoundsOfMap(currentProjection, ctx) {
            let path = d3.geoPath().projection(currentProjection).context(ctx).pointRadius(7);
            let vWidth = view.width;
            let vHeight = view.height;

            ctx.beginPath();
            path({ type: "Sphere" });
            ctx.rect(0, 0, vWidth, vHeight);
            ctx.fillStyle = "rgb(0,0,5,1.0)";
            ctx.fill("evenodd");
            ctx.closePath();

        }
    }

    function drawContourEmphasis(grid, projection) {
        const header = grid.header;
        if (header === "") return;

        const ctx = d3.select("#contourEmphasis").node().getContext("2d");
        let φ0 = header.la1;  // the grid's origin (e.g., 0.0E, 90.0N)
        φ0 = φ0 < 0 ? -φ0 : φ0;
        const epsilon = 0.;
        const ni = header.nx, njj = header.ny;    // number of grid points W-E and N-S (e.g., 144 x 73)
        const widthLo = 360;
        const lo1 = header.lo1;
        const loRotate = lo1 - 180, loStart = lo1 - 180, loStop = lo1 + 180,
            loStartOffset = loStart + epsilon, loStopOffset = loStop - epsilon;

        const thresholds = grid.contourEmphasisThresholds;

        let contours = d3.contours()
            .size([ni + 1, njj])
            .smooth(true)
            .thresholds(thresholds);

        let contourPath = d3.geoPath()
            .projection(projection)
            .context(ctx);

        let overlayGridPoints = [];
        let ix = 0;

        grid.forEachPoint2(function (λ, φ, d) {
            if (µ.isValue(d)) {
                overlayGridPoints[ix++] = d;
            }
        });

        overlayGridPoints = rotate(overlayGridPoints);
        overlayGridPoints = cylinder(overlayGridPoints);

        // convert 0~360 to -180~180
        function rotate(values) {
            let v = new Float32Array(njj * ni);
            for (let j = 0; j < njj; j++) {
                for (let i = 0; i < ni; i++) {
                    let value = values[j * ni + i];
                    if (value.length === 2) { // velocity
                        v[j * ni + i] = Math.sqrt(value[0] * value[0] + value[1] * value[1]);
                    } else {
                        v[j * ni + i] = value;
                    }
                }
            }

            let l = ni >> 1;
            for (let j = 0, k = 0; j < njj; ++j, k += ni) {
                v = reverseArr(v.subarray(k, k + l), v, k, k + l);
                v = reverseArr(v.subarray(k + l, k + ni), v, k + l, k + ni);
                v = reverseArr(v.subarray(k, k + ni), v, k, k + ni);
            }
            return v;
        }

        // reverse array not available on IE
        function reverseArr(input, source, start, end) {
            let v = new Float32Array(input.length);

            for (let i = input.length - 1, j = 0; i >= 0; i--) {
                v[j++] = input[i];
            }

            for (let i = 0, k = 0; i < source.length; i++) {
                if (i >= start && i <= (end - 1)) {
                    source[i] = v[k++];
                }
                if (i > (end - 1)) {
                    break;
                }
            }

            return source;
        }


        // duplicate left column to the right for stitch
        function cylinder(values) {
            let v = new Float32Array(njj * (ni + 1));
            for (let j = 0; j < njj; j++) {
                for (let i = 0; i < ni; i++) {
                    v[j * (ni + 1) + i] = values[j * ni + i];
                }
                v[j * (ni + 1) + ni] = values[j * ni];
            }
            return v;
        }

        const gradients = grid.contourEmphasisGradients;

        contours(overlayGridPoints)
            .map(function (d) { // invert x/y pixel points to lon/lan coords
                var shared = {};
                var p = {
                    type: "Polygon",
                    coordinates: d3.merge(d.coordinates.map(function (polygon) {
                        return polygon.map(function (ring) {
                            return ring.map(function (point) {
                                // var coordX = point[0] / (ni + 1) * lo2 - loRotate;
                                return [point[0] / (ni + 1) * widthLo + loRotate, φ0 - point[1] / njj * 180];
                                // return [point[0] / (ni + 1) * widthLo, φ0 - point[1] / njj * 180];
                            }).reverse();
                        });
                    }))
                };

                // // // Add contact points with the antimeridian.
                p.coordinates = p.coordinates
                    .map(function (ring) {
                        ring.pop();
                        var r = [];
                        for (var i = 0, l = ring.length; i < l; i++) {
                            var p = ring[i];
                            if (p[0] === loStop && ring[(i + l - 1) % l][0] < loStop)
                                r.push([p[0], p[1] + epsilon]);
                            if (p[0] === loStart && ring[(i + l - 1) % l][0] > loStart)
                                r.push([p[0], p[1] - epsilon]);
                            r.push(p);
                            if (p[0] === loStop && ring[(i + 1) % l][0] < loStop)
                                r.push([p[0], p[1] - epsilon]);
                            if (p[0] === loStart && ring[(i + 1) % l][0] > loStart)
                                r.push([p[0], p[1] + epsilon]);
                        }
                        r.push(r[0]);
                        return r;
                    });

                // Record the y-intersections with the antimeridian.
                p.coordinates.forEach(function (ring) {
                    ring.forEach(function (p) {
                        if (p[0] === loStart || p[0] === loStop) {
                            shared[p[1]] |= p[0] === loStart ? 1 : 2;
                        }
                    });
                });

                // Offset any unshared antimeridian points to prevent their stitching.
                p.coordinates.forEach(function (ring) {
                    ring.forEach(function (p) {
                        if ((p[0] === loStart || p[0] === loStop) && shared[p[1]] !== 3) {
                            p[0] = p[0] === loStart ? loStartOffset : loStopOffset;
                        }
                    });
                });

                p = geoStitch(p, loStart, loStop);

                // If the MultiPolygon is empty, treat it as the Sphere.
                return p.coordinates.length
                    ? { type: "Polygon", coordinates: p.coordinates, value: d.value }
                    : { type: "Sphere", value: d.value };

            })
            .forEach(function (geometry, i) { // draw geometry coords to path (projection, canvas context)
                if (geometry.type === "Polygon") {
                    ctx.beginPath();
                    contourPath(geometry);
                    ctx.closePath();
                    ctx.lineJoin = "round";
                    ctx.lineWidth = 3;
                    const gradient = gradients(geometry.value);
                    var strokeStyle = "rgba(" + gradient[0] + "," + gradient[1] + "," + gradient[2] + "," + gradient[3] + ")"; //"rgba(170,240,250,1.0)";
                    ctx.strokeStyle = strokeStyle;
                    ctx.stroke();
                }
            });
    }

    function drawWindVector(projectionType, grid, currentProjection) {
        const ctx = d3.select("#windVector").node().getContext("2d");
        const header = grid.header;
        const ni = header.nx;    // number of grid points W-E and N-S (e.g., 144 x 73)
        // const nj = header.ny;
        const arrowSize = 8;
        let maxSpeed = 0, minSpeed = 0, count = 0;

        grid.forEachPoint(function (λ, φ, d) {
            let wind = Math.sqrt(d[0] * d[0] + d[1] * d[1]);
            if (count === 0) {
                maxSpeed = wind;
                minSpeed = wind;
            }
            if (µ.isValue(wind)) {
                if (maxSpeed < wind) {
                    maxSpeed = wind;
                }
                if (minSpeed > wind) {
                    minSpeed = wind;
                }
            }
            count++;
        });

        let windScale = d3.scaleLinear().domain([minSpeed, maxSpeed]).range([1, 8]);
        let centerCoord = currentProjection.invert([view.width / 2, view.height / 2]);
        let gridRateKey = ni === 432 ? 2 : ni === 144 ? 0.4 : 1;
        let windVectorRate = 2300 / 300;  // when current scale is about 300, wind vector counts are about 2300
        let conversion = Math.sqrt(300 / currentProjection.scale()) * Math.sqrt(gridRateKey); // variable for scale rate by map and mode
        let xRate = parseInt(windVectorRate / 2.53 * conversion) === 0 ? 1 : parseInt(windVectorRate / 2.53 * conversion),
            yRate = parseInt(windVectorRate / 1.89 * conversion) === 0 ? 1 : parseInt(windVectorRate / 1.89 * conversion);

        let i = 0, j = 0;
        grid.forEachPoint(function (λ, φ, d) {
            let wind = Math.sqrt(d[0] * d[0] + d[1] * d[1]);
            if (µ.isValue(wind) && (parseInt(i % xRate) === 0) && ((parseInt(i / ni) % yRate) === 0)) {
                let distance = d3.geoDistance([λ, φ], centerCoord);

                if (!(projectionType === "orthographic" && distance > 1.57)) { // except for wind behind the earth
                    drawVectorArrow(ctx, currentProjection, λ, φ, d, wind, arrowSize, windScale);
                }
                j++;
            }
            i++;
        });
    }

    function drawVectorArrow(ctx, currentProjection, λ, φ, d, wind, arrowSize, windScale) {
        let pos = currentProjection([λ, φ]);
        let angle = Math.atan2(-d[1], d[0]);

        ctx.save();
        ctx.translate(pos[0], pos[1]);
        ctx.rotate(angle);
        ctx.beginPath();
        ctx.strokeStyle = "rgba(255,255,255,0.8)";
        ctx.moveTo(-arrowSize / 3 * windScale(wind), 0);
        ctx.lineTo(arrowSize / 3 * windScale(wind), 0);
        ctx.lineTo(arrowSize / 5, arrowSize / 6);
        ctx.moveTo(arrowSize / 2, 0);
        ctx.lineTo(arrowSize / 5, -arrowSize / 6);
        ctx.stroke();
        ctx.closePath();

        ctx.restore();
    }

    function contour(n, m, values, type) {
        // let min = Math.min.apply(null, values);
        // let max = Math.max.apply(null, values);
        let min = 0, max = 0;
        min = max = values[0];

        for (let i = 0; i < values.length; i++) {
            if (type !== "wind" && values[i] === 0) continue;
            min = min > values[i] ? values[i] : min;
            max = max < values[i] ? values[i] : max;
        }

        //temp의 경우 invalid값이 존재함
        //invalid값을 0으로 넣으면 contour가 이상하게 그려짐
        //invalid값을 max보다 약간 높은 값을 줌으로서 contour가 좀 더 자연스럽게 그려지도록 유도
        if (type !== "wind") {
            for (let i = 0; i < values.length; i++) {
                if (values[i] === 0) values[i] = max + 0.1;
            }
        }

        let diff = Math.abs(min - max),
            thresholdsMax = diff / 100.,
            thresholdsValue = thresholdsMax * 5;
// console.log(min,max,thresholdsValue, d3.range(min, max , thresholdsValue).concat([max + 0.1]));
        let contours = d3.contours()
            .size([n, m])
            .smooth(true)
            .thresholds(type !== "wind" ? d3.range(min, max, thresholdsValue).concat([max + 0.1]) : d3.range(min, max, thresholdsValue)) //max+0.1은 invalid값을 표현한 숫자다
            (values);

        return contours;
    }

    function areaMeanTimeseries(data) {
        if (data === null || data === undefined || data === "") {
            µ.removeChildren(d3.select("#areaMean").node());
            return;
        }

        let maxFcst = data.fcst.reduce(function (previous, current) {
            return previous > current ? previous : current;
        });

        let minFcst = data.fcst.reduce(function (previous, current) {
            return previous > current ? current : previous;
        });

        let maxHcst = data.hcst.reduce(function (previous, current) {
            return previous > current ? previous : current;
        });

        let minHcst = data.hcst.reduce(function (previous, current) {
            return previous > current ? current : previous;
        });

        let maxAnomaly = data.anomaly.reduce(function (previous, current) {
            return previous > current ? previous : current;
        });

        let minAnomaly = data.anomaly.reduce(function (previous, current) {
            return previous > current ? current : previous;
        });

        let min = minFcst > minHcst ? minHcst : minFcst;
        let max = maxFcst > maxHcst ? maxFcst : maxHcst;

        const marginRatio = window.innerWidth * 0.125;
        const margin = { top: marginRatio, right: marginRatio, bottom: marginRatio, left: marginRatio }
            , width = window.innerWidth - margin.left - margin.right // Use the window's width
            , height = window.innerHeight - margin.top - margin.bottom; // Use the window's height

        // The number of datapoints, unit
        const n = data.fcst.length;
        const unit = data.unit[0];

        // console.log("unit", unit);

        let xScale = d3.scaleLinear()
            .domain([0, n * unit - unit]) // input
            .range([0, width]); // output

        let yScale = d3.scaleLinear()
            .domain([min, max]) // input
            .range([height, 0]); // output

        let yScaleAnomaly = d3.scaleLinear()
            // .domain([0, 1]) // input
            .domain([minAnomaly, maxAnomaly]) // input
            .range([height, 0]); // output

        let line = d3.line()
            .x(function (d, i) { return xScale(d.x); }) // set the x values for the line generator
            .y(function (d) { return yScale(d.y); }) // set the y values for the line generator
            .curve(d3.curveMonotoneX) // apply smoothing to the line

        let lineAnomaly = d3.line()
            .x(function (d, i) { return xScale(d.x); }) // set the x values for the line generator
            .y(function (d) { return yScaleAnomaly(d.y); }) // set the y values for the line generator
            .curve(d3.curveMonotoneX) // apply smoothing to the line

        let i = -1 * unit;
        let datasetFcst = data.fcst.map(function (d) {
            return { x: i += unit, y: d };
        });
        i = -1 * unit;
        let datasetHcst = data.hcst.map(function (d) {
            return { x: i += unit, y: d };
        });
        i = -1 * unit;
        let datasetAnomaly = data.anomaly.map(function (d) {
            return { x: i += unit, y: d };
        });

        µ.removeChildren(d3.select("#areaMean").node());

        let series = ["fcst", "hcst", "anomaly"];
        let colors = ["#0000ff", "#00ff00", "#ff0000"];

        let svg = d3.select("#areaMean")
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

        svg.append("rect")
            .attr("width", width)
            .attr("height", height)
            .attr("class", "rect");

        svg.append("g")
            .attr("class", "axis")
            .attr("transform", "translate(0," + height + ")")
            .call(d3.axisBottom(xScale)
                // .ticks(n/10)
                .tickSize(-height)
            ); // Create an axis component with d3.axisBottom

        svg.append("g")
            .attr("class", "axis")
            .call(d3.axisLeft(yScale)
                .tickSize(-width)
            ); // Create an axis component with d3.axisLeft

        svg.append("g")
            .attr("class", "axis")
            .attr("transform", "translate( " + width + ", 0 )")
            .call(d3.axisRight(yScaleAnomaly)
                // .tickSize(-width)
            ); // Create an axis component with d3.axisLeft

        svg.append("path")
            .datum(datasetFcst) // 10. Binds data to the line
            .attr("class", "fcst") // Assign a class for styling
            .attr("d", line); // 11. Calls the line generator

        svg.append("path")
            .datum(datasetHcst) // 10. Binds data to the line
            .attr("class", "hcst") // Assign a class for styling
            .attr("d", line); // 11. Calls the line generator

        svg.append("path")
            .datum(datasetAnomaly) // 10. Binds data to the line
            .attr("class", "anomaly") // Assign a class for styling
            .attr("d", lineAnomaly); // 11. Calls the line generator

        // legend
        let legend = svg.append("g")
            .attr("text-anchor", "end")
            .selectAll("g")
            .data(series)
            .enter().append("g")
            .attr("transform", function (d, i) {
                return "translate(0," + i * 20 + ")";
            });

        legend.append("rect")
            .attr("x", width - 22)
            .attr("y", height - 65)
            .attr("width", 19)
            .attr("height", 19)
            .attr("fill", function (d, i) {
                return colors[i];
            });

        legend.append("text")
            .attr("x", width - 30)
            .attr("y", height - 50)
            .attr("class", "legendText")
            // .attr("dy", "0.32em")
            .text(function (d) {
                return d;
            });
    }

    return {
        drawContour: drawContour,
        drawWindVector: drawWindVector,
        contour: contour,
        areaMeanTimeseries: areaMeanTimeseries,
        drawContourEmphasis: drawContourEmphasis,
    };

})();